// Section 20: at the end of every Winter the eight conditions are tested; a passing year
// increments the steady-state counter, a failing one resets it and names the first failure.
// Loss conditions are checked every season.
import { isChild, peopleSorted, settlementTrust, ageYears } from '../../core/queries';
import { forEachSorted } from '../../core/sorted';
import type {
  HouseholdId,
  LossReason,
  PhaseContext,
  SteadyCondition,
  SteadyResult,
} from '../../core/types';
import { departHousehold } from '../shared/depart';
import { steadyConditions } from './conditions';

export function checkSteady(ctx: PhaseContext): void {
  const d = ctx.draft;
  const conditions = steadyConditions(ctx);
  const failed =
    (Object.keys(conditions) as SteadyCondition[]).find((k) => !conditions[k].pass) ?? null;
  d.steady.counter = failed === null ? d.steady.counter + 1 : 0;
  d.steady.lastFailedCondition = failed;
  const result: SteadyResult = {
    turn: ctx.season.turn,
    year: ctx.season.year,
    conditions,
    allPass: failed === null,
    counter: d.steady.counter,
    target: d.options.steadyTargetYears,
  };
  d.steady.lastResult = result;
  ctx.report.current.steady = result;
  ctx.report.add({ kind: 'steady', result });
  if (d.outcome === null && !d.sandbox && d.steady.counter >= d.options.steadyTargetYears) {
    d.outcome = { kind: 'won', turn: ctx.season.turn, reason: 'steady-state' };
    ctx.report.add({ kind: 'outcome', outcome: 'won', reason: 'steady-state' });
  }
}

function fertileAdultsRemain(ctx: PhaseContext): boolean {
  const l = ctx.cfg.lifecycle;
  const people = peopleSorted(ctx.state);
  const woman = people.some(
    (p) =>
      p.sex === 'female' &&
      ageYears(ctx.cfg, p.ageSeasons) >= l.conceptionMinAgeYears &&
      ageYears(ctx.cfg, p.ageSeasons) <= l.conceptionMaxAgeYears,
  );
  const man = people.some(
    (p) => p.sex === 'male' && ageYears(ctx.cfg, p.ageSeasons) >= l.conceptionMinAgeYears,
  );
  return woman && man;
}

export function checkLoss(ctx: PhaseContext): void {
  const d = ctx.draft;
  const loss = ctx.cfg.loss;
  const people = peopleSorted(ctx.state);
  const adults = people.filter((p) => !isChild(ctx.cfg, p)).length;
  d.counters.lowTrustStreak =
    people.length > 0 && settlementTrust(ctx.state) < loss.breakdownTrust
      ? d.counters.lowTrustStreak + 1
      : 0;
  let reason: LossReason | null = null;
  if (adults < loss.minAdults || people.length < loss.minPopulation) reason = 'population-collapse';
  else if (
    !fertileAdultsRemain(ctx) &&
    ctx.season.turn - (d.counters.lastArrivalTurn ?? 0) >= loss.noFertileNoArrivalSeasons
  ) {
    reason = 'no-fertile-adults';
  } else if (d.counters.lowTrustStreak >= loss.breakdownSeasons) reason = 'breakdown';
  // Loss is checked every season (Section 20), in sandbox too: a won run played on in sandbox
  // that collapses ends there, as lost (M6-12).
  if (reason === null || (d.outcome !== null && !d.sandbox)) return;
  if (reason === 'breakdown') breakdown(ctx);
  d.outcome = { kind: 'lost', turn: ctx.season.turn, reason };
  d.sandbox = false;
  ctx.report.add({ kind: 'outcome', outcome: 'lost', reason });
}

/**
 * Section 20 breakdown: "households leave en masse and the player is deposed". Every household
 * whose members' mean trust is below the breakdown threshold leaves (reason `breakdown`); the
 * run then ends with the Ledger. Settlement trust is below the threshold, so at least one does.
 */
function breakdown(ctx: PhaseContext): void {
  const below = ctx.cfg.loss.breakdownTrust;
  const leaving: HouseholdId[] = [];
  forEachSorted(ctx.state.households, (h) => {
    let total = 0;
    let n = 0;
    for (const id of h.memberIds) {
      const p = ctx.state.people[id];
      if (p === undefined) continue;
      total += p.trust;
      n++;
    }
    if (n > 0 && total / n < below) leaving.push(h.id);
  });
  for (const id of leaving) departHousehold(ctx, id, 'breakdown');
}
